import { ReactNode } from "react";

/**
 * The navigation icons, ported path for path from the handoff. The reference draws them with
 * Lucide, the app ships Heroicons, and the two disagree on every one of them — a folder is not a
 * locker, a circled check is not a checked square. Rather than add a second icon dependency for
 * six glyphs, the paths come straight from the prototype at their own weight: 19px, stroke 1.8,
 * both already tokens.
 */
const NavIcon = ({ children }: { children: ReactNode }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4.75 h-4.75 shrink-0"
    aria-hidden="true"
  >
    {children}
  </svg>
);

/** Unequal panes, not a 2×2 grid: a dashboard is made of blocks of different weights. */
export const DashboardIcon = () => (
  <NavIcon>
    <rect x="3" y="3" width="7" height="9" rx="1.4" />
    <rect x="14" y="3" width="7" height="5" rx="1.4" />
    <rect x="14" y="12" width="7" height="9" rx="1.4" />
    <rect x="3" y="16" width="7" height="5" rx="1.4" />
  </NavIcon>
);

/** A locker, not a folder — the storage holds documents, it does not classify them. */
export const StorageIcon = () => (
  <NavIcon>
    <rect x="8.5" y="2.5" width="7" height="4.5" rx="1.2" />
    <rect x="6" y="7" width="12" height="14.5" rx="3" />
    <path d="M10 11.5h4" />
  </NavIcon>
);

/** A class is a group of people, not a graduation cap. */
export const ClassesIcon = () => (
  <NavIcon>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
  </NavIcon>
);

export const AgendaIcon = () => (
  <NavIcon>
    <rect x="3" y="4" width="18" height="17" rx="2.5" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </NavIcon>
);

/** The check escapes the square: a task is closed, not merely marked. */
export const TasksIcon = () => (
  <NavIcon>
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </NavIcon>
);

export const SettingsIcon = () => (
  <NavIcon>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </NavIcon>
);
